import { useEffect, useState } from "react"
import { TrendingUp, TrendingDown, Loader2, Pencil, Trash2, Plus, Check, X } from "lucide-react"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useCurrency } from "@/context/currency/currency-context"
import hive, { updatePositionDrip } from "@/lib/api/hive"
import type { Transaction, DividendReceived, Portfolio } from "@/types/portfolio"
import type { Stock } from "@/types/stock"
import { calculatePositionMetrics } from "@/lib/transaction-aggregator"
import { isEffectiveDripEnabled } from "@/lib/portfolio-utils"
import { Analytics } from "@/lib/analytics"
import { cn, formatShares } from "@/lib/utils"
import { Skeleton } from "@/components/ui/skeleton"
import { DividendHistoryRowSkeleton } from "@/components/custom/skeletons"

interface StockDetailSheetProps {
	isOpen: boolean
	onClose: () => void
	stock: Stock
	portfolioId: string
	token: string
	totalShares: number
	averagePrice: number
	totalCost: number
	onTransactionChanged?: () => void
	portfolio?: Portfolio
}

export function StockDetailSheet({
	isOpen,
	onClose,
	stock,
	portfolioId,
	token,
	totalShares,
	averagePrice,
	totalCost,
	onTransactionChanged,
	portfolio,
}: StockDetailSheetProps) {
	const { localFormat } = useCurrency()
	const [transactions, setTransactions] = useState<Transaction[]>([])
	const [totalTransactions, setTotalTransactions] = useState(0)
	const [loading, setLoading] = useState(true)
	const [loadingMore, setLoadingMore] = useState(false)
	const [editingId, setEditingId] = useState<string | null>(null)
	const [showAddForm, setShowAddForm] = useState(false)
	const [dividendHistory, setDividendHistory] = useState<DividendReceived[]>([])
	const [dividendHistoryLoading, setDividendHistoryLoading] = useState(false)

	// Safety detail — disabled until EODHD fundamentals plan is active

	// DRIP settings state
	const [dripSetting, setDripSetting] = useState<"ON" | "OFF" | "INHERIT">("INHERIT")
	const [dripTaxRate, setDripTaxRate] = useState("")
	const [taxOverride, setTaxOverride] = useState(false)
	const [savingDrip, setSavingDrip] = useState(false)

	// Edit form state
	const [editType, setEditType] = useState<"BUY" | "SELL">("BUY")
	const [editAmount, setEditAmount] = useState("")
	const [editPrice, setEditPrice] = useState("")
	const [editDate, setEditDate] = useState("")

	// Add form state
	const [addType, setAddType] = useState<"BUY" | "SELL">("BUY")
	const [addAmount, setAddAmount] = useState("")
	const [addPrice, setAddPrice] = useState("")
	const [addDate, setAddDate] = useState("")

	const fetchTransactions = async () => {
		setLoading(true)
		try {
			const result = await hive.getTransactions(portfolioId, stock.ticker, token)
			setTransactions(result.payload)
			setTotalTransactions(result.total)
		} catch (error) {
			console.error("Failed to fetch transactions:", error)
		} finally {
			setLoading(false)
		}
	}

	const loadMoreTransactions = async () => {
		setLoadingMore(true)
		try {
			const result = await hive.getTransactions(portfolioId, stock.ticker, token, 50, transactions.length)
			setTransactions((prev) => [...prev, ...result.payload])
			setTotalTransactions(result.total)
		} catch (error) {
			console.error("Failed to load more transactions:", error)
		} finally {
			setLoadingMore(false)
		}
	}

	useEffect(() => {
		if (!isOpen) return
		fetchTransactions()
		// Fetch dividend income history
		const fetchDividends = async () => {
			setDividendHistoryLoading(true)
			try {
				const history = await hive.getDividendIncomeHistory(portfolioId, stock.ticker, stock.exchange, token)
				setDividendHistory(history)
			} catch {
				setDividendHistory([])
			} finally {
				setDividendHistoryLoading(false)
			}
		}
		fetchDividends()
		// Safety detail fetch disabled — EODHD fundamentals plan required
		// Initialize DRIP settings from position data
		const pos = portfolio?.positions?.find((p) => p.ticker === stock.ticker && p.exchange === stock.exchange)
		setDripSetting(pos?.drip_setting ?? "INHERIT")
		const hasTaxOverride = pos?.tax_withholding_rate != null
		setTaxOverride(hasTaxOverride)
		setDripTaxRate(hasTaxOverride ? String(pos.tax_withholding_rate) : "")
	}, [isOpen, portfolioId, stock.ticker, stock.exchange, token])

	const handleStartEdit = (txn: Transaction) => {
		if (txn.type !== "BUY" && txn.type !== "SELL") return
		setEditingId(txn.id)
		setEditType(txn.type)
		setEditAmount(txn.shares.toString())
		setEditPrice(txn.price_per_share.toString())
		setEditDate(txn.date.split("T")[0])
	}

	const handleCancelEdit = () => {
		setEditingId(null)
		setEditType("BUY")
		setEditAmount("")
		setEditPrice("")
		setEditDate("")
	}

	const handleSaveEdit = async (transactionId: string) => {
		const parsedShares = parseFloat(editAmount)
		const parsedPrice = parseFloat(editPrice)
		if (isNaN(parsedShares) || parsedShares <= 0 || isNaN(parsedPrice) || parsedPrice <= 0) {
			alert("Shares and price must be positive numbers")
			return
		}

		try {
			await hive.updateTransaction(
				portfolioId,
				transactionId,
				{
					type: editType,
					shares: parsedShares,
					price: parsedPrice,
					date: new Date(editDate).toISOString(),
				},
				token,
			)
			await fetchTransactions()
			handleCancelEdit()
			Analytics.capture("Transaction Edited")
			onTransactionChanged?.()
		} catch (error) {
			console.error("Failed to update transaction:", error)
			alert("Failed to update transaction")
		}
	}

	const handleDelete = async (transactionId: string) => {
		if (!confirm("Are you sure you want to delete this transaction?")) return

		try {
			await hive.deleteTransaction(portfolioId, transactionId, token)
			await fetchTransactions()
			Analytics.capture("Transaction Deleted")
			onTransactionChanged?.()
		} catch (error) {
			console.error("Failed to delete transaction:", error)
			alert("Failed to delete transaction")
		}
	}

	const handleAddTransaction = async () => {
		if (!addAmount || parseFloat(addAmount) <= 0) return
		if (addPrice && parseFloat(addPrice) <= 0) return

		try {
			const fallbackPrice = stock.price > 0 ? stock.price : undefined
			await hive.createTransaction(
				portfolioId,
				{
					ticker: stock.ticker,
					exchange: stock.exchange,
					type: addType,
					shares: parseFloat(addAmount),
					price: addPrice ? parseFloat(addPrice) : fallbackPrice,
					date: addDate ? new Date(addDate).toISOString() : undefined,
				},
				token,
			)
			await fetchTransactions()
			setShowAddForm(false)
			setAddType("BUY")
			setAddAmount("")
			setAddPrice("")
			setAddDate("")
			onTransactionChanged?.()
		} catch (error) {
			console.error("Failed to add transaction:", error)
			alert("Failed to add transaction")
		}
	}

	const handleSaveDrip = async () => {
		setSavingDrip(true)
		try {
			await updatePositionDrip(
				portfolioId,
				stock.ticker,
				stock.exchange,
				{
					drip_setting: dripSetting,
					...(taxOverride
						? { tax_withholding_rate: dripTaxRate ? parseFloat(dripTaxRate) : 0 }
						: { clear_tax_override: true }),
				},
				token,
			)
			onTransactionChanged?.()
		} catch (err) {
			console.error("Failed to update DRIP settings:", err)
			alert("Failed to update DRIP settings")
		} finally {
			setSavingDrip(false)
		}
	}

	const pos = portfolio?.positions?.find((p) => p.ticker === stock.ticker && p.exchange === stock.exchange)
	const savedDripSetting = pos?.drip_setting ?? "INHERIT"
	const effectiveDrip =
		dripSetting === savedDripSetting
			? (pos?.effective_drip_enabled ?? isEffectiveDripEnabled({ drip_setting: dripSetting }, portfolio))
			: isEffectiveDripEnabled({ drip_setting: dripSetting }, portfolio)

	const metrics = calculatePositionMetrics(
		{
			ticker: stock.ticker,
			exchange: stock.exchange,
			totalShares,
			averagePrice,
			totalCost,
			transactions,
		},
		stock.price,
	)

	const isPositive = metrics.totalReturn >= 0

	return (
		<Sheet open={isOpen} onOpenChange={onClose}>
			<SheetContent className="w-full sm:max-w-lg overflow-y-auto">
				<SheetHeader>
					<SheetTitle className="text-2xl">{stock.ticker}</SheetTitle>
					<SheetDescription>{stock.name}</SheetDescription>
				</SheetHeader>

				<div className="mt-6 space-y-6">
					{/* Current Price Card */}
					<Card className="bg-gradient-to-br from-sky-50 to-sky-100 dark:from-sky-950 dark:to-sky-900 border-sky-200 dark:border-sky-800">
						<CardHeader className="pb-2">
							<CardTitle className="text-sm text-muted-foreground">Current Market Price</CardTitle>
						</CardHeader>
						<CardContent>
							{stock.price === 0 ? (
								<div className="space-y-2">
									<Skeleton className="h-9 w-40" />
									<Skeleton className="h-4 w-24" />
								</div>
							) : (
								<div className="flex items-baseline gap-2">
									<span className="text-3xl font-bold text-sky-950 dark:text-sky-50">
										{localFormat(stock.price, stock.currency)}
									</span>
									<span className="text-sm text-muted-foreground">
										Yield: {stock.dividendYield.toFixed(2)}%
									</span>
								</div>
							)}
						</CardContent>
					</Card>

					{/* Dividend Safety Card */}
					<Card>
						<CardHeader className="pb-3">
							<CardTitle className="text-sm font-medium">Dividend Safety</CardTitle>
						</CardHeader>
						<CardContent>
							<p className="text-sm text-muted-foreground italic">
								Coming soon — dividend safety scores will analyze payout ratios, debt levels, cash flow coverage, and earnings trends to rate each position's dividend sustainability.
							</p>
						</CardContent>
					</Card>

					{/* Your Position Card */}
					<Card>
						<CardHeader>
							<CardTitle>Your Position</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							<div className="grid grid-cols-2 gap-4">
								<div>
									<p className="text-sm text-muted-foreground">Shares</p>
									<p className="text-2xl font-bold">{formatShares(totalShares)}</p>
								</div>
								<div>
									<p className="text-sm text-muted-foreground">Avg Price</p>
									<p className="text-2xl font-bold">
										{localFormat(averagePrice, stock.currency)}
									</p>
								</div>
							</div>

							<div className="pt-3 border-t">
								<div className="flex justify-between items-center mb-2">
									<span className="text-sm text-muted-foreground">Total Cost</span>
									<span className="font-semibold">
										{localFormat(totalCost, stock.currency)}
									</span>
								</div>
								<div className="flex justify-between items-center mb-2">
									<span className="text-sm text-muted-foreground">Current Value</span>
									<span className="font-semibold">
										{localFormat(metrics.currentValue, stock.currency)}
									</span>
								</div>
								<div className="flex justify-between items-center pt-2 border-t">
									<span className="text-sm text-muted-foreground">Price Return</span>
									<div className="flex items-center gap-2">
										{isPositive ? (
											<TrendingUp className="h-4 w-4 text-green-600" />
										) : (
											<TrendingDown className="h-4 w-4 text-red-600" />
										)}
										<span className={`font-bold ${isPositive ? "text-green-600" : "text-red-600"}`}>
											{localFormat(metrics.totalReturn, stock.currency)} (
											{metrics.returnPercentage.toFixed(2)}%)
										</span>
									</div>
								</div>
								{dividendHistory.length > 0 &&
									(() => {
										const totalDivsReceived = dividendHistory.reduce(
											(sum, d) => sum + d.total_amount,
											0,
										)
										const trueTotalReturn = metrics.currentValue - totalCost + totalDivsReceived
										const trueTotalReturnPct =
											totalCost > 0 ? (trueTotalReturn / totalCost) * 100 : 0
										const isTruePositive = trueTotalReturn >= 0
										return (
											<div className="flex justify-between items-center pt-2">
												<span className="text-sm font-medium">Total Return</span>
												<div className="flex items-center gap-2">
													{isTruePositive ? (
														<TrendingUp className="h-4 w-4 text-green-600" />
													) : (
														<TrendingDown className="h-4 w-4 text-red-600" />
													)}
													<span
														className={`font-bold ${isTruePositive ? "text-green-600" : "text-red-600"}`}
													>
														{localFormat(trueTotalReturn, stock.currency)} (
														{trueTotalReturnPct.toFixed(2)}%)
													</span>
												</div>
											</div>
										)
									})()}
							</div>
						</CardContent>
					</Card>

					{/* DRIP Settings */}
					<Card>
						<CardHeader>
							<CardTitle className="text-sm">DRIP Settings</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							<div className="space-y-1.5">
								<Label className="text-xs">Reinvestment</Label>
								<Select
									value={dripSetting}
									onValueChange={(v) => setDripSetting(v as "ON" | "OFF" | "INHERIT")}
								>
									<SelectTrigger className="h-9">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="INHERIT">Inherit from Portfolio</SelectItem>
										<SelectItem value="ON">DRIP On</SelectItem>
										<SelectItem value="OFF">DRIP Off</SelectItem>
									</SelectContent>
								</Select>
								<p className="text-[10px] text-muted-foreground">
									Effective:{" "}
									<span
										className={cn(
											"font-semibold",
											effectiveDrip
												? "text-green-600 dark:text-green-400"
												: "text-muted-foreground",
										)}
									>
										{effectiveDrip ? "ON" : "OFF"}
									</span>
								</p>
							</div>
							<div className="space-y-1.5">
								<Label className="text-xs">Tax Withholding (%)</Label>
								{taxOverride ? (
									<>
										<Input
											type="text"
											inputMode="decimal"
											value={dripTaxRate}
											onChange={(e) => setDripTaxRate(e.target.value)}
											placeholder="0"
											className="h-9 w-24"
										/>
										<button
											type="button"
											className="text-[10px] text-sky-600 dark:text-sky-400 hover:underline"
											onClick={() => {
												setTaxOverride(false)
												setDripTaxRate("")
											}}
										>
											Reset to portfolio default
										</button>
									</>
								) : (
									<>
										<div className="flex items-center gap-2">
											<Input
												type="text"
												value={String(
													pos?.effective_tax_withholding_rate ??
														portfolio?.tax_withholding_rate ??
														0,
												)}
												readOnly
												className="h-9 w-24 bg-muted text-muted-foreground cursor-default"
											/>
											<span className="text-[10px] text-muted-foreground">(from portfolio)</span>
										</div>
										<button
											type="button"
											className="text-[10px] text-sky-600 dark:text-sky-400 hover:underline"
											onClick={() => {
												setTaxOverride(true)
												setDripTaxRate(
													String(
														pos?.effective_tax_withholding_rate ??
															portfolio?.tax_withholding_rate ??
															0,
													),
												)
											}}
										>
											Customize
										</button>
									</>
								)}
							</div>
							<Button
								size="sm"
								onClick={handleSaveDrip}
								disabled={savingDrip}
								className="bg-sky-600 hover:bg-sky-700"
							>
								{savingDrip ? "Saving..." : "Save DRIP"}
							</Button>
						</CardContent>
					</Card>

					{/* Dividend Income History */}
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
							<CardTitle>Dividend Income</CardTitle>
							{dividendHistory.length > 0 && (
								<span className="text-sm font-bold text-sky-600 dark:text-sky-400">
									{localFormat(
										dividendHistory.reduce((sum, d) => sum + d.total_amount, 0),
										stock.currency,
									)}
								</span>
							)}
						</CardHeader>
						<CardContent>
							{dividendHistoryLoading ? (
								<div className="space-y-2">
									{[0, 1, 2].map((i) => (
										<DividendHistoryRowSkeleton key={i} />
									))}
								</div>
							) : dividendHistory.length === 0 ? (
								<p className="text-center text-muted-foreground py-4 text-sm">
									No dividend income recorded yet
								</p>
							) : (
								<div className="space-y-2">
									{dividendHistory.map((d, idx) => (
										<div
											key={idx}
											className={cn(
												"p-2 rounded-lg border",
												d.drip_processed
													? "border-green-200 dark:border-green-900/30 bg-green-50/30 dark:bg-green-900/10"
													: "border-sky-100 dark:border-sky-900/30 bg-sky-50/30 dark:bg-sky-900/10",
											)}
										>
											<div className="flex justify-between items-center">
												<div className="text-sm">
													<span className="text-muted-foreground">
														{new Date(d.payment_date).toLocaleDateString("en-US", {
															year: "numeric",
															month: "short",
															day: "numeric",
														})}
													</span>
													<span className="text-xs text-muted-foreground ml-2">
														{localFormat(d.amount_per_share, d.currency ?? stock.currency)}
														/share x {formatShares(d.shares_held)}
													</span>
												</div>
												<div className="flex items-center gap-2">
													<span className="font-semibold text-sm text-sky-900 dark:text-sky-100">
														{localFormat(d.total_amount, d.currency ?? stock.currency)}
													</span>
													{d.drip_processed ? (
														<span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
															DRIP
														</span>
													) : (
														<span
															className={cn(
																"text-[10px] px-1.5 py-0.5 rounded-full font-medium",
																d.status === "paid"
																	? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
																	: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400",
															)}
														>
															{d.status === "paid" ? "Cash" : "Upcoming"}
														</span>
													)}
												</div>
											</div>
											{d.drip_processed && d.shares_added != null && (
												<div className="mt-1 text-xs text-green-700 dark:text-green-400">
													+{formatShares(d.shares_added)} shares @{" "}
													{localFormat(d.drip_price_per_share ?? 0, d.currency ?? stock.currency)}
													/share
													{d.tax_withheld != null && d.tax_withheld > 0 && (
														<span className="text-muted-foreground ml-2">
															Tax:{" "}
															{localFormat(d.tax_withheld, d.currency ?? stock.currency)}
														</span>
													)}
													{d.fee_charged != null && d.fee_charged > 0 && (
														<span className="text-muted-foreground ml-2">
															Fee:{" "}
															{localFormat(d.fee_charged, d.currency ?? stock.currency)}
														</span>
													)}
												</div>
											)}
										</div>
									))}
								</div>
							)}
						</CardContent>
					</Card>

					{/* Transaction History */}
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
							<CardTitle>Transaction History</CardTitle>
							<Button
								size="sm"
								onClick={() => setShowAddForm(!showAddForm)}
								className="bg-sky-600 hover:bg-sky-700"
							>
								<Plus className="h-4 w-4 mr-1" />
								Add
							</Button>
						</CardHeader>
						<CardContent>
							{/* Add Transaction Form */}
							{showAddForm && (
								<div className="mb-4 p-4 border rounded-lg bg-sky-50 dark:bg-sky-950 space-y-3">
									<div className="flex gap-2">
										<Button
											size="sm"
											variant={addType === "BUY" ? "default" : "outline"}
											onClick={() => setAddType("BUY")}
											className={cn(addType === "BUY" && "bg-green-600 hover:bg-green-700")}
										>
											Buy
										</Button>
										<Button
											size="sm"
											variant={addType === "SELL" ? "default" : "outline"}
											onClick={() => setAddType("SELL")}
											className={cn(addType === "SELL" && "bg-red-600 hover:bg-red-700")}
										>
											Sell
										</Button>
									</div>
									<div className="grid grid-cols-2 gap-2">
										<div>
											<Label htmlFor="add-amount" className="text-xs">
												Shares
											</Label>
											<Input
												id="add-amount"
												type="number"
												value={addAmount}
												onChange={(e) => setAddAmount(e.target.value)}
												placeholder="Amount"
												className="h-10"
											/>
										</div>
										<div>
											<Label htmlFor="add-price" className="text-xs">
												Price
											</Label>
											<Input
												id="add-price"
												type="number"
												value={addPrice}
												onChange={(e) => setAddPrice(e.target.value)}
												placeholder="Optional"
												min="0.01"
												className="h-10"
											/>
										</div>
									</div>
									<div>
										<Label htmlFor="add-date" className="text-xs">
											Date
										</Label>
										<Input
											id="add-date"
											type="date"
											value={addDate}
											onChange={(e) => setAddDate(e.target.value)}
											className="h-10"
										/>
									</div>
									<div className="flex gap-2">
										<Button
											size="sm"
											onClick={handleAddTransaction}
											disabled={!addAmount || parseFloat(addAmount) <= 0}
											className="flex-1 bg-sky-600 hover:bg-sky-700"
										>
											<Check className="h-4 w-4 mr-1" />
											Add
										</Button>
										<Button size="sm" variant="outline" onClick={() => setShowAddForm(false)}>
											<X className="h-4 w-4" />
										</Button>
									</div>
								</div>
							)}

							{loading ? (
								<div className="flex items-center justify-center py-8">
									<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
								</div>
							) : transactions.length === 0 ? (
								<p className="text-center text-muted-foreground py-8">No transactions yet</p>
							) : (
								<div className="space-y-2">
									{transactions.map((txn: Transaction) => (
										<div
											key={txn.id}
											className={cn(
												"p-3 rounded-lg border",
												txn.type === "SPLIT"
													? "border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-950/30"
													: txn.type === "DRIP"
														? "border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30"
														: txn.type === "BUY"
															? "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950"
															: "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950",
											)}
										>
											{editingId === txn.id ? (
												<div className="space-y-2">
													<div className="flex gap-2">
														<Button
															size="sm"
															variant={editType === "BUY" ? "default" : "outline"}
															onClick={() => setEditType("BUY")}
															className={cn(
																"flex-1",
																editType === "BUY" && "bg-green-600 hover:bg-green-700",
															)}
														>
															Buy
														</Button>
														<Button
															size="sm"
															variant={editType === "SELL" ? "default" : "outline"}
															onClick={() => setEditType("SELL")}
															className={cn(
																"flex-1",
																editType === "SELL" && "bg-red-600 hover:bg-red-700",
															)}
														>
															Sell
														</Button>
													</div>
													<div className="grid grid-cols-2 gap-2">
														<div>
															<Label htmlFor="edit-amount" className="text-xs">
																Shares
															</Label>
															<Input
																id="edit-amount"
																type="number"
																value={editAmount}
																onChange={(e) => setEditAmount(e.target.value)}
																className="h-10"
															/>
														</div>
														<div>
															<Label htmlFor="edit-price" className="text-xs">
																Price
															</Label>
															<Input
																id="edit-price"
																type="number"
																value={editPrice}
																onChange={(e) => setEditPrice(e.target.value)}
																min="0.01"
																className="h-10"
															/>
														</div>
													</div>
													<div>
														<Label htmlFor="edit-date" className="text-xs">
															Date
														</Label>
														<Input
															id="edit-date"
															type="date"
															value={editDate}
															onChange={(e) => setEditDate(e.target.value)}
															className="h-10"
														/>
													</div>
													<div className="flex gap-2">
														<Button
															size="sm"
															onClick={() => handleSaveEdit(txn.id)}
															className="flex-1 bg-green-600 hover:bg-green-700"
														>
															<Check className="h-4 w-4 mr-1" />
															Save
														</Button>
														<Button size="sm" variant="outline" onClick={handleCancelEdit}>
															<X className="h-4 w-4" />
														</Button>
													</div>
												</div>
											) : (
												<>
													<div className="flex justify-between items-start mb-1">
														<div>
															<span
																className={cn(
																	"text-sm font-semibold",
																	txn.type === "SPLIT"
																		? "text-blue-700 dark:text-blue-400"
																		: txn.type === "DRIP"
																			? "text-amber-700 dark:text-amber-400"
																			: txn.type === "BUY"
																				? "text-green-700 dark:text-green-400"
																				: "text-red-700 dark:text-red-400",
																)}
															>
																{txn.type === "SPLIT" && txn.split_label
																	? `SPLIT ${txn.split_label}`
																	: txn.type}
															</span>
															<span className="text-sm text-muted-foreground ml-2">
																{new Date(txn.date).toLocaleDateString("en-US", {
																	year: "numeric",
																	month: "short",
																	day: "numeric",
																})}
															</span>
														</div>
														<div className="flex items-center gap-1">
															{txn.type !== "SPLIT" && (
																<span className="text-sm font-medium mr-2">
																	{formatShares(txn.shares)} shares
																</span>
															)}
															{txn.type !== "DRIP" && txn.type !== "SPLIT" && (
																<>
																	<Button
																		size="sm"
																		variant="ghost"
																		onClick={() => handleStartEdit(txn)}
																		className="h-6 w-6 p-0"
																	>
																		<Pencil className="h-3 w-3" />
																	</Button>
																	<Button
																		size="sm"
																		variant="ghost"
																		onClick={() => handleDelete(txn.id)}
																		className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
																	>
																		<Trash2 className="h-3 w-3" />
																	</Button>
																</>
															)}
														</div>
													</div>
													{txn.type === "SPLIT" ? (
													<div className="text-xs text-blue-700 dark:text-blue-400">
														Stock split — shares adjusted automatically
													</div>
												) : (
													<div className="flex justify-between items-center text-xs text-muted-foreground">
														<span>
															@{" "}
															{localFormat(txn.price_per_share, stock.currency)}
															/share
														</span>
														<span className="font-semibold">
															{localFormat(txn.shares * txn.price_per_share, stock.currency)}
														</span>
													</div>
												)}
													{txn.type === "DRIP" && txn.gross_dividend != null && (
														<div className="mt-1 text-xs text-amber-700 dark:text-amber-400">
															Gross:{" "}
															{localFormat(txn.gross_dividend, stock.currency)}
															{txn.tax_withheld != null && txn.tax_withheld > 0 && (
																<span className="text-muted-foreground ml-2">
																	Tax:{" "}
																	{localFormat(txn.tax_withheld, stock.currency)}
																</span>
															)}
															{txn.fee_charged != null && txn.fee_charged > 0 && (
																<span className="text-muted-foreground ml-2">
																	Fee:{" "}
																	{localFormat(txn.fee_charged, stock.currency)}
																</span>
															)}
															<span className="ml-2">
																Net:{" "}
																{localFormat(txn.shares * txn.price_per_share, stock.currency)}
															</span>
														</div>
													)}
												</>
											)}
										</div>
									))}
								</div>
							)}
							{transactions.length < totalTransactions && (
								<div className="mt-3 text-center">
									<Button
										size="sm"
										variant="outline"
										onClick={loadMoreTransactions}
										disabled={loadingMore}
									>
										{loadingMore ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
										Load More ({transactions.length} of {totalTransactions})
									</Button>
								</div>
							)}
						</CardContent>
					</Card>
				</div>
			</SheetContent>
		</Sheet>
	)
}
